import React from 'react';
import { Route, Redirect, RouteProps } from 'react-router-dom';
import { IonSpinner } from '@ionic/react';
import { useAuth } from '../contexts/AuthContext';
import { AuthenticationState } from '../contexts/AuthContext';
import { useSubscription, SubscriptionFeature } from '../contexts/SubscriptionContext';

interface ProtectedRouteProps extends Omit<RouteProps, 'component'> {
    component: React.ComponentType<any>;
    requiredFeature?: SubscriptionFeature;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    component: Component,
    requiredFeature,
    ...rest
}) => {
    const { authState } = useAuth();
    const { subscriptionState, canAccessFeature, isSubscriptionBlocked } = useSubscription();

    return (
        <Route
            {...rest}
            render={props => {
                // First handle authentication states
                switch (authState) {
                    case AuthenticationState.INITIALIZING:
                        return (
                            <div className="ion-text-center ion-padding">
                                <IonSpinner />
                            </div>
                        );
                    case AuthenticationState.REQUIRES_NEW_PASSWORD:
                        return (
                            <Redirect
                                to={{
                                    pathname: '/change-password',
                                    state: { from: props.location }
                                }}
                            />
                        );
                    case AuthenticationState.BLOCKED:
                        return (
                            <Redirect
                                to={{
                                    pathname: '/blocked',
                                    state: { from: props.location }
                                }}
                            />
                        );
                    case AuthenticationState.UNAUTHENTICATED:
                        return (
                            <Redirect
                                to={{
                                    pathname: '/login',
                                    state: { from: props.location }
                                }}
                            />
                        );
                }

                // Then handle subscription states
                if (!subscriptionState.isReady) {
                    return (
                        <div className="ion-text-center ion-padding">
                            <IonSpinner />
                        </div>
                    );
                }

                if (isSubscriptionBlocked) {
                    return (
                        <Redirect
                            to={{
                                pathname: '/account',
                                state: { from: props.location }
                            }}
                        />
                    );
                }

                // If a feature is required, check if the user can access it
                if (requiredFeature && !canAccessFeature(requiredFeature)) {
                    return (
                        <Redirect
                            to={{
                                pathname: '/subscription',
                                state: { from: props.location }
                            }}
                        />
                    );
                }

                // If all checks pass, render the component
                return <Component {...props} />;
            }}
        />
    );
}; 